import { ReactNode, useState, SVGProps } from "react";
import {
  Avatar,
  Box,
  Button,
  ClickAwayListener,
  createStyles,
  makeStyles,
  ListItem as MuiLisItem,
  Popper,
  SvgIcon,
  Theme,
  Toolbar,
  Typography,
  ListItemIcon,
} from "@material-ui/core";
import Skeleton from "@material-ui/lab/Skeleton";
import clsx from "clsx";

import { NotificationAlert, Notification, Arrow } from "~components/Icons";

export * from "./CustomerSelector";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      position: "sticky",
      top: 0,
      padding: theme.spacing(2),
      backgroundColor: "white",
      boxShadow: "0 1px 1px 0 rgba(32, 37, 43, 0.12), 0 0 1px 0 rgba(32, 37, 43, 0.1)",
      zIndex: theme.zIndex.appBar,
    },
    toolbar: {
      padding: 0,
      minHeight: "auto",
    },
    title: {
      color: theme.palette.dark.main,
      lineHeight: "36px",
      marginRight: theme.spacing(4),
    },
    avatar: {
      height: 48,
      width: 48,
      backgroundColor: "white",
      boxShadow: "0 4px 8px 0 rgba(32, 37, 43, 0.12), 0 0 1px 0 rgba(32, 37, 43, 0.1)",

      "& span": {
        color: theme.palette.primary.main,
        fontSize: theme.spacing(2),
        fontWeight: 600,
        lineHeight: "21px",
        letterSpacing: "normal",
      },
    },
    profileButton: {
      padding: 0,

      "&:hover": {
        backgroundColor: "transparent",
      },
    },
    profileTag: {
      marginLeft: 12,
      fontSize: 12,
      fontWeight: 700,
      lineHeight: 1.6,
      letterSpacing: "1px",
    },
    notificationButton: {
      minWidth: "auto",
      marginLeft: 32,
      fontSize: 24,
      color: theme.palette.coolGrey.main,
      padding: theme.spacing(1.5),
      background: "white",
      boxShadow: "0 4px 8px 0 rgba(32, 37, 43, 0.12), 0 0 1px 0 rgba(32, 37, 43, 0.1)",
      borderRadius: theme.spacing(0.5),

      "& svg": {
        fontSize: 24,
      },
    },
    popover: {
      marginTop: 8,
      maxWidth: 368,
      maxHeight: "calc(100vh - 72px)",
      overflowY: "auto",

      backgroundColor: "white",

      borderRadius: "4px",
      boxShadow: "0 18px 18px 0 rgba(32, 37, 43, 0.12), 0 0 1px 0 rgba(32, 37, 43, 0.1)",

      zIndex: theme.zIndex.modal,
    },
    userMenuItem: {
      display: "flex",
      alignItems: "center",
      background: "transparent",
      border: 0,
      padding: `${theme.spacing(1.5)}px ${theme.spacing(2)}px`,

      "& span": {
        fontSize: 12,
        fontWeight: "bold",
        lineHeight: "19px",
        letterSpacing: 1,
        textTransform: "uppercase",
      },

      "& svg": {
        fontSize: 24,
        color: theme.palette.coolGrey.main,
      },
    },
    notificationMenuItem: {
      padding: `${theme.spacing(2)}px ${theme.spacing(3)}px`,

      "& svg": {
        fontSize: 24,
      },

      "& h1": {
        fontSize: 12,
        fontWeight: "bold",
        lineHeight: 1.6,
        letterSpacing: 1,
        textTransform: "uppercase",
      },

      "& p": {
        fontSize: 12,
        fontWeight: "normal",
        lineHeight: 1.6,
        letterSpacing: 0,
      },

      "& button": {
        color: theme.palette.primary.main,
        fontSize: 12,
        fontWeight: "bold",
        lineHeight: 1.6,
        letterSpacing: 1,
        textTransform: "uppercase",
        padding: 0,
        borderRadius: 0,

        "&:hover": {
          background: "transparent",
          boxShadow: `0 1px 0 0 ${theme.palette.primary.main}`,
        },
        "&:active": {
          color: theme.palette.primary.dark,
          boxShadow: `0 1px 0 0 ${theme.palette.primary.dark}`,
        },
      },
    },
    menuItemBorder: {
      borderTop: `1px solid ${theme.palette.background.default}`,
    },
    icon: {
      marginRight: theme.spacing(1.25),
      minWidth: theme.spacing(3),

      color: theme.palette.coolGrey.main,
    },
    successIcon: {
      color: theme.palette.success.main,
    },
    errorIcon: {
      color: theme.palette.error.main,
    },
    warningIcon: {
      color: theme.palette.warning.main,
    },
  }),
);

export type UserMenu = {
  i18nName: string;
  icon?: (props: SVGProps<SVGSVGElement>) => JSX.Element;
  action: () => void;
}[];

export type Notifications = {
  title: string;
  content: ReactNode;
  icon: (props: SVGProps<SVGSVGElement>) => JSX.Element;
  action: () => void;
  actionLabel: string;
  status: "success" | "error" | "warning";
}[];

export type HeaderProps = {
  title?: string;
  username: string;
  avatarSrc?: string;
  avatarLetters?: string;
  isLoading: boolean;
  notifications: Notifications;
  userMenu: UserMenu;
  customerSelector?: ReactNode;
};

export const Header = ({
  title,
  isLoading,
  username,
  avatarSrc,
  avatarLetters,
  userMenu,
  notifications,
  customerSelector,
}: HeaderProps) => {
  const classes = useStyles();
  const [notificationsEl, setNotificationsEl] = useState<null | HTMLElement>(null);
  const [userMenuEl, setUserMenuEl] = useState<null | HTMLElement>(null);

  const handleNotificationClick = (event: React.MouseEvent<HTMLElement>) => {
    setNotificationsEl(notificationsEl ? null : event.currentTarget);
  };

  const handleUserMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setUserMenuEl(userMenuEl ? null : event.currentTarget);
  };

  const showNotifications = Boolean(notificationsEl);
  const showUserMenu = Boolean(userMenuEl);
  const notificationId = showNotifications ? "notifications-popover" : undefined;
  const userMenuId = showUserMenu ? "usermenu-popover" : undefined;

  return (
    <Box className={classes.root}>
      <Toolbar className={classes.toolbar}>
        <Typography variant="h2" className={classes.title}>
          {title}
        </Typography>
        {customerSelector}
        <Box flexGrow={1} />
        <Box display="flex">
          <ClickAwayListener onClickAway={() => setUserMenuEl(null)}>
            <Box
              display="flex"
              component={Button}
              className={classes.profileButton}
              alignItems={isLoading ? "center" : "baseline"}
              onClick={handleUserMenuClick}
            >
              {isLoading ? (
                <>
                  <Skeleton variant="circle" width={48} height={48} />
                  <Skeleton width={108} height={19} style={{ margin: "0 24px 0 12px" }} />
                </>
              ) : (
                <>
                  <Avatar className={classes.avatar} {...(avatarSrc && { src: avatarSrc })}>
                    <Typography component="span">{!avatarSrc && avatarLetters}</Typography>
                  </Avatar>
                  <Typography component="span" className={classes.profileTag}>
                    {username}
                  </Typography>
                  <SvgIcon
                    component={Arrow}
                    color="primary"
                    style={{
                      marginLeft: "12px",
                      fontSize: "16px",
                      alignSelf: "center",
                      transform: `rotate(${true ? -180 : 0}deg)`,
                      transition: "transform ease-in-out .2s",
                    }}
                  />
                  <Popper
                    id={userMenuId}
                    open={showUserMenu}
                    anchorEl={userMenuEl}
                    placement="bottom-end"
                    className={classes.popover}
                  >
                    <Box display="flex" flexDirection="column">
                      {userMenu?.map((menuItem, i) => (
                        <MuiLisItem
                          component={Button}
                          onClick={menuItem.action}
                          key={`userMenuItem-${menuItem.i18nName}`}
                          className={clsx(classes.userMenuItem, {
                            [classes.menuItemBorder]: i > 0,
                          })}
                        >
                          <ListItemIcon className={classes.icon}>
                            {menuItem?.icon ? <SvgIcon component={menuItem.icon} /> : null}
                          </ListItemIcon>
                          {menuItem.i18nName}
                        </MuiLisItem>
                      ))}
                    </Box>
                  </Popper>
                </>
              )}
            </Box>
          </ClickAwayListener>
          <ClickAwayListener onClickAway={() => setNotificationsEl(null)}>
            <Box>
              {isLoading ? (
                <>
                  <Skeleton variant="rect" width={48} height={48} style={{ borderRadius: "4px" }} />
                </>
              ) : (
                <>
                  <Button onClick={handleNotificationClick} className={classes.notificationButton}>
                    <SvgIcon
                      component={Boolean(notifications?.length) ? NotificationAlert : Notification}
                    />
                  </Button>
                  <Popper
                    id={notificationId}
                    open={showNotifications}
                    anchorEl={notificationsEl}
                    placement="bottom-end"
                    className={classes.popover}
                    modifiers={{
                      flip: {
                        enabled: false,
                      },
                    }}
                  >
                    <Box>
                      {Boolean(notifications.length) ? (
                        notifications?.map((menuItem, i) => (
                          <Box
                            onClick={menuItem.action}
                            key={`menuItem-${menuItem.title}`}
                            className={clsx(classes.notificationMenuItem, {
                              [classes.menuItemBorder]: i > 0,
                            })}
                          >
                            <Box display="flex" alignItems="center">
                              <ListItemIcon
                                className={`${classes.icon} ${
                                  classes[
                                    `${menuItem.status}Icon` as
                                      | "successIcon"
                                      | "warningIcon"
                                      | "errorIcon"
                                  ]
                                }`}
                              >
                                {menuItem?.icon ? <SvgIcon component={menuItem.icon} /> : null}
                              </ListItemIcon>
                              <Typography component="h1">{menuItem.title}</Typography>
                            </Box>
                            <Box style={{ paddingLeft: 32 }}>
                              <Typography component="p">{menuItem.content}</Typography>
                              <Button onClick={menuItem.action}>{menuItem.actionLabel}</Button>
                            </Box>
                          </Box>
                        ))
                      ) : (
                        <Box className={classes.notificationMenuItem}>
                          <Typography component="h1">No notifications at the moment</Typography>
                        </Box>
                      )}
                    </Box>
                  </Popper>
                </>
              )}
            </Box>
          </ClickAwayListener>
        </Box>
      </Toolbar>
    </Box>
  );
};
